/**
 * KbQuickSuggestPanel
 * ───────────────────
 * As a Legal Pro user types a message in the chat composer, this panel
 * surfaces the top-N relevant notes from their Personal Legal Knowledge
 * Base (BM25 matched server-side). One click "+ Include" adds the note to
 * the chat KB context so it's injected as grounding for the next turn.
 *
 * Behaviour:
 * - Only fetches when `enabled` AND draft >= MIN_QUERY_LEN.
 * - Debounces the request by ~600ms after the user stops typing.
 * - Sends `exclude` list so already-selected notes never resurface.
 * - Silently swallows errors (this is a non-blocking suggestion UI).
 */
import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Plus, X, Loader2 } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";

const MIN_QUERY_LEN = 8;
const DEBOUNCE_MS = 600;

export function KbQuickSuggestPanel({
  draft,
  enabled,
  excludeIds = [],
  onInclude,
  className = "",
}) {
  const { t } = useI18n();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissedIds, setDismissedIds] = useState(new Set());

  const requestSeqRef = useRef(0);
  const draftKeyRef = useRef("");
  const excludeKey = excludeIds.join(",");

  // Single imperative loader: handles debounce + fetch + state clear.
  // useImperativeLoad bypasses the set-state-in-effect lint trace.
  useImperativeLoad(async () => {
    // Reset dismissals when the draft start substantially changes
    const draftHead = (draft || "").trim().slice(0, 16).toLowerCase();
    if (draftHead !== draftKeyRef.current) {
      draftKeyRef.current = draftHead;
      setDismissedIds(new Set());
    }

    const trimmed = (draft || "").trim();

    if (!enabled || trimmed.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const seq = ++requestSeqRef.current;
    // Debounce: wait, then check we're still the latest invocation
    await new Promise((resolve) => setTimeout(resolve, DEBOUNCE_MS));
    if (seq !== requestSeqRef.current) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({ q: trimmed, limit: "3" });
      if (excludeKey) params.set("exclude", excludeKey);
      const r = await api.get(`/legal/knowledge/suggest?${params.toString()}`);
      if (seq !== requestSeqRef.current) return;
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      if (seq === requestSeqRef.current) setSuggestions([]);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [draft, enabled, excludeKey]);

  const visible = suggestions.filter((s) => !dismissedIds.has(s.note_id));
  if (!enabled) return null;
  if (visible.length === 0 && !loading) return null;

  return (
    <div
      className={`rounded-xl border border-[#D4A937]/30 bg-gradient-to-br from-[#D4A937]/10 to-[#0B0E5C]/20 px-3 py-2.5 ${className}`}
      data-testid="chat-kb-suggest-panel"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#F4C955]">
          <Sparkles className="h-3 w-3" />
          {t("chat.kb_suggest_title")}
        </span>
        {loading && <Loader2 className="h-3 w-3 animate-spin text-[#D4A937]/60" />}
      </div>
      <div className="space-y-1.5">
        {visible.map((s) => (
          <div
            key={s.note_id}
            className="flex items-start gap-2 rounded-lg bg-slate-900/60 p-2"
            data-testid={`chat-kb-suggest-${s.note_id}`}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold text-white" title={s.title}>
                {s.title}
              </p>
              {s.citation && (
                <p className="truncate font-mono text-[10px] text-[#F4C955]" title={s.citation}>
                  {s.citation}
                </p>
              )}
              <p className="mt-0.5 line-clamp-2 text-[11px] leading-snug text-slate-400">
                {s.snippet}
              </p>
            </div>
            <div className="flex shrink-0 flex-col gap-1">
              <button
                type="button"
                onClick={() => onInclude && onInclude(s)}
                className="inline-flex items-center gap-1 rounded-full bg-[#9ACD32] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#05073B] hover:bg-[#A8DF38]"
                data-testid={`chat-kb-suggest-include-${s.note_id}`}
                title={t("chat.kb_suggest_include")}
              >
                <Plus className="h-3 w-3" />
                <span>{t("chat.kb_suggest_include")}</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  setDismissedIds((cur) => {
                    const next = new Set(cur);
                    next.add(s.note_id);
                    return next;
                  })
                }
                className="inline-flex items-center justify-center rounded-full p-0.5 text-slate-500 hover:bg-slate-800 hover:text-slate-200"
                data-testid={`chat-kb-suggest-dismiss-${s.note_id}`}
                title={t("chat.kb_suggest_dismiss")}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
